import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/apiClient';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

const SUPPORTED_INDEX_OPTIONS = [
  { code: 'SP500', label: 'S&P 500' },
  { code: 'NASDAQ100', label: 'Nasdaq-100' },
  { code: 'DOW30', label: 'Dow 30' },
  { code: 'TSX60', label: 'S&P/TSX 60' },
  { code: 'TSX_COMPOSITE', label: 'S&P/TSX Composite' },
];

const EMPTY_SNAPSHOT_CARDS = {
  market_data: {
    price_updated_at: null,
    current_fundamentals_updated_at: null,
    quarterly_fundamentals_updated_at: null,
    coverage_status: 'Coverage not recorded',
  },
  scanner_snapshot: { current_snapshot_at: null },
  strategy_lab_snapshot: { publication_created_at: null, anchor_date: null },
};

function formatAdminTimestamp(value) {
  if (!value) return 'Not recorded';
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  }).format(timestamp);
}

function formatHistoricalAnchorDate(value) {
  return value || 'Not recorded';
}

const CREATE_ACTION_RULES = {
  ticker_change: {
    clearNewTicker: false,
    clearConversionRatio: true,
    showNewTicker: true,
    showConversionRatio: false,
    sendNewTicker: true,
    validationLabel: 'ticker changes',
    requireNewTicker: true,
    requireConversionRatio: false,
    rejectSameTicker: true,
  },
  stock_for_stock_merger: {
    clearNewTicker: false,
    clearConversionRatio: false,
    showNewTicker: true,
    showConversionRatio: true,
    sendNewTicker: true,
    validationLabel: 'stock-for-stock mergers',
    requireNewTicker: true,
    requireConversionRatio: true,
    rejectSameTicker: true,
  },
  split: {
    clearNewTicker: true,
    clearConversionRatio: false,
    showNewTicker: false,
    showConversionRatio: true,
    sendNewTicker: false,
    validationLabel: 'stock splits',
    requireNewTicker: false,
    requireConversionRatio: true,
    rejectSameTicker: false,
  },
  reverse_split: {
    clearNewTicker: true,
    clearConversionRatio: false,
    showNewTicker: false,
    showConversionRatio: true,
    sendNewTicker: false,
    validationLabel: 'stock splits',
    requireNewTicker: false,
    requireConversionRatio: true,
    rejectSameTicker: false,
  },
  spin_off: {
    clearNewTicker: false,
    clearConversionRatio: false,
    showNewTicker: true,
    showConversionRatio: true,
    sendNewTicker: true,
    validationLabel: 'spin-offs',
    requireNewTicker: true,
    requireConversionRatio: true,
    rejectSameTicker: true,
  },
  delisting: {
    clearNewTicker: true,
    clearConversionRatio: true,
    showNewTicker: false,
    showConversionRatio: false,
    sendNewTicker: false,
    validationLabel: 'delistings',
    requireNewTicker: false,
    requireConversionRatio: false,
    rejectSameTicker: false,
  },
};

const CREATE_ACTION_TYPES = Object.keys(CREATE_ACTION_RULES);

const CORPORATE_ACTION_EXPLANATIONS = {
  ticker_change: 'Changes a holding from the old ticker to the new ticker.',
  stock_for_stock_merger: 'Moves the holding to the new company and adjusts the share count using the conversion ratio.',
  split: 'Increases the share count and lowers the average price per share according to the split ratio.',
  reverse_split: 'Reduces the share count and increases the average price per share according to the reverse-split ratio.',
  spin_off: 'Keeps the original holding and adds shares of the spun-off company.',
  delisting: 'Marks affected holdings inactive for review. It does not sell them or retire the ticker from scanner tracking.',
};

function getCreateActionValidationError({
  actionType,
  oldTicker,
  newTicker,
  effectiveDate,
  conversionRatioInput,
  conversionRatio,
}) {
  const rule = CREATE_ACTION_RULES[actionType];

  if (!rule) {
    return '';
  }

  if (!oldTicker) {
    return `Old ticker is required for ${rule.validationLabel}`;
  }

  if (!effectiveDate) {
    return `Effective date is required for ${rule.validationLabel}`;
  }

  if (rule.requireNewTicker && !newTicker) {
    return `New ticker is required for ${rule.validationLabel}`;
  }

  if (rule.rejectSameTicker && newTicker === oldTicker) {
    return `New ticker must be different from old ticker for ${rule.validationLabel}`;
  }

  if (rule.requireConversionRatio && !conversionRatioInput) {
    return `Conversion ratio is required for ${rule.validationLabel}`;
  }

  if (rule.requireConversionRatio && (!Number.isFinite(conversionRatio) || conversionRatio <= 0)) {
    return `Conversion ratio must be a positive number for ${rule.validationLabel}`;
  }

  return '';
}

function AdminDataQuality() {
  const [issues, setIssues] = useState([]);
  const [summary, setSummary] = useState({
    total_issues: 0,
    affected_users_total: 0,
    affected_portfolios_total: 0,
    affected_holdings_total: 0,
  });
  const [snapshotCards, setSnapshotCards] = useState(EMPTY_SNAPSHOT_CARDS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedIssue, setExpandedIssue] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  
  // Corporate Actions state
  const [corporateActions, setCorporateActions] = useState([]);
  const [corporateActionsLoading, setCorporateActionsLoading] = useState(true);
  const [corporateActionsError, setCorporateActionsError] = useState('');
  const [createFormData, setCreateFormData] = useState({
    action_type: 'ticker_change',
    old_ticker: '',
    new_ticker: '',
    effective_date: '',
    announcement_date: '',
    conversion_ratio: '',
    status: 'draft',
    source_name: '',
    source_url: '',
    source_notes: '',
    admin_notes: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');

  // Manual holding adjustment state
  const [manualSearch, setManualSearch] = useState({
    ticker: '',
    holding_id: '',
  });
  const [manualHoldings, setManualHoldings] = useState([]);
  const [manualSelectedHolding, setManualSelectedHolding] = useState(null);
  const [manualFormData, setManualFormData] = useState({
    quantity: '',
    average_buy_price: '',
    ticker: '',
    is_active: 'true',
    admin_notes: '',
  });
  const [manualDeleteConfirmation, setManualDeleteConfirmation] = useState('');
  const [manualLoading, setManualLoading] = useState(false);
  const [manualPreviewLoading, setManualPreviewLoading] = useState(false);
  const [manualApplyLoading, setManualApplyLoading] = useState(false);
  const [manualDeleteLoading, setManualDeleteLoading] = useState(false);
  const [manualError, setManualError] = useState('');
  const [manualSuccess, setManualSuccess] = useState('');
  const [manualPreview, setManualPreview] = useState(null);

  // Explicit current ticker-tracking controls
  const [trackingRetireForm, setTrackingRetireForm] = useState({ ticker: '', admin_notes: '' });
  const [trackingMembershipForm, setTrackingMembershipForm] = useState({ ticker: '', index_code: 'SP500', admin_notes: '' });
  const [trackingAddForm, setTrackingAddForm] = useState({
    ticker: '', index_code: 'SP500', company_name: '', exchange: '', country: '', currency: '',
    asset_type: 'equity', data_source_symbol: '', priority: '', source_note: '', admin_notes: '',
  });
  const [trackingPreviews, setTrackingPreviews] = useState({ retire: null, remove: null, add: null });
  const [trackingLoading, setTrackingLoading] = useState({ retire: false, remove: false, add: false });
  const [trackingApplyLoading, setTrackingApplyLoading] = useState({ retire: false, remove: false, add: false });
  const [trackingErrors, setTrackingErrors] = useState({ retire: '', remove: '', add: '' });
  const [trackingSuccess, setTrackingSuccess] = useState({ retire: '', remove: '', add: '' });
  
  // Dry-run preview state
  const [dryRunCache, setDryRunCache] = useState({});
  const [dryRunLoading, setDryRunLoading] = useState({});
  const [dryRunError, setDryRunError] = useState({});
  const [expandedDryRun, setExpandedDryRun] = useState(null);
  
  // Apply state
  const [applyLoading, setApplyLoading] = useState({});
  const [applyError, setApplyError] = useState({});
  const [applySuccess, setApplySuccess] = useState({});
  const [applyResult, setApplyResult] = useState({});
  
  const navigate = useNavigate();

  const handleReviewClick = async (issue) => {
    if (expandedIssue && expandedIssue.ticker === issue.ticker && expandedIssue.issue_type === issue.issue_type) {
      setExpandedIssue(null);
      setDetailError('');
      return;
    }

    // Check if we already have cached detail for this ticker
    if (detailCache[issue.ticker]) {
      setExpandedIssue(issue);
      setDetailError('');
      return;
    }

    // Fetch detail for this ticker
    setDetailLoading(true);
    setDetailError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/data-quality/issues/${issue.ticker}`, { method: 'GET' });

      if (!response.ok) {
        throw new Error('Failed to fetch ticker detail');
      }

      const detail = await response.json();
      setDetailCache(prev => ({ ...prev, [issue.ticker]: detail }));
      setExpandedIssue(issue);
    } catch (err) {
      setDetailError(err.message || 'Failed to load ticker detail');
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    const fetchDataQualityIssues = async () => {
      try {
        const response = await apiFetch(`${API_BASE_URL}/admin/data-quality/issues`, { method: 'GET' });

        if (response.status === 403) {
          navigate('/dashboard');
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to fetch data quality issues');
        }

        const data = await response.json();

        setIssues(data.issues || []);
        setSummary({
          total_issues: data.total_issues || 0,
          affected_users_total: data.affected_users_total || 0,
          affected_portfolios_total: data.affected_portfolios_total || 0,
          affected_holdings_total: data.affected_holdings_total || 0,
        });
        setSnapshotCards({
          ...EMPTY_SNAPSHOT_CARDS,
          ...(data.snapshot_cards || {}),
          market_data: {
            ...EMPTY_SNAPSHOT_CARDS.market_data,
            ...(data.snapshot_cards?.market_data || {}),
          },
          scanner_snapshot: {
            ...EMPTY_SNAPSHOT_CARDS.scanner_snapshot,
            ...(data.snapshot_cards?.scanner_snapshot || {}),
          },
          strategy_lab_snapshot: {
            ...EMPTY_SNAPSHOT_CARDS.strategy_lab_snapshot,
            ...(data.snapshot_cards?.strategy_lab_snapshot || {}),
          },
        });
      } catch (err) {
        setError(err.message || 'Failed to load data quality issues');
      } finally {
        setLoading(false);
      }
    };

    fetchDataQualityIssues();
  }, [navigate]);

  const fetchCorporateActions = useCallback(async () => {
    setCorporateActionsLoading(true);
    setCorporateActionsError('');
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/corporate-actions`, { method: 'GET' });

      if (response.status === 403) {
        navigate('/dashboard');
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch corporate actions');
      }

      const data = await response.json();
      setCorporateActions(data || []);
    } catch (err) {
      setCorporateActionsError(err.message || 'Failed to load corporate actions');
    } finally {
      setCorporateActionsLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchCorporateActions();
  }, [fetchCorporateActions]);

  const handleCreateFormChange = (field, value) => {
    if (field === 'action_type' && CREATE_ACTION_TYPES.includes(value)) {
      const createActionRule = CREATE_ACTION_RULES[value];

      setCreateFormData(prev => ({
        ...prev,
        action_type: value,
        new_ticker: createActionRule.clearNewTicker ? '' : prev.new_ticker,
        conversion_ratio: createActionRule.clearConversionRatio ? '' : prev.conversion_ratio,
      }));
      setCreateError('');
      setCreateSuccess('');
      return;
    }

    setCreateFormData(prev => ({ ...prev, [field]: value }));
    setCreateError('');
    setCreateSuccess('');
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');

    const oldTicker = createFormData.old_ticker.trim().toUpperCase();
    const newTicker = createFormData.new_ticker.trim().toUpperCase();
    const conversionRatio = Number(createFormData.conversion_ratio);
    const createValidationError = getCreateActionValidationError({
      actionType: createFormData.action_type,
      oldTicker,
      newTicker,
      effectiveDate: createFormData.effective_date,
      conversionRatioInput: createFormData.conversion_ratio,
      conversionRatio,
    });

    if (createValidationError) {
      setCreateError(createValidationError);
      return;
    }

    setCreateLoading(true);

    try {
      // Build request payload, excluding empty optional fields
      const payload = {
        action_type: createFormData.action_type,
        old_ticker: oldTicker,
        effective_date: createFormData.effective_date,
        status: createFormData.status,
      };

      // Add optional fields only if they have values
      if (
        createFormData.new_ticker.trim()
        && (CREATE_ACTION_RULES[createFormData.action_type]?.sendNewTicker
          ?? !['delisting', 'split', 'reverse_split'].includes(createFormData.action_type))
      ) {
        payload.new_ticker = newTicker;
      }
      if (createFormData.announcement_date) {
        payload.announcement_date = createFormData.announcement_date;
      }
      if (createFormData.action_type === 'ticker_change') {
        payload.conversion_ratio = 1.0;
      } else if (createFormData.conversion_ratio && createFormData.action_type !== 'delisting') {
        payload.conversion_ratio = parseFloat(createFormData.conversion_ratio);
      }
      if (createFormData.source_name.trim()) {
        payload.source_name = createFormData.source_name.trim();
      }
      if (createFormData.source_url.trim()) {
        payload.source_url = createFormData.source_url.trim();
      }
      if (createFormData.source_notes.trim()) {
        payload.source_notes = createFormData.source_notes.trim();
      }
      if (createFormData.admin_notes.trim()) {
        payload.admin_notes = createFormData.admin_notes.trim();
      }

      const response = await apiFetch(`${API_BASE_URL}/admin/corporate-actions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create corporate action');
      }

      setCreateSuccess('Corporate action created successfully');
      
      // Clear form
      setCreateFormData({
        action_type: 'ticker_change',
        old_ticker: '',
        new_ticker: '',
        effective_date: '',
        announcement_date: '',
        conversion_ratio: '',
        status: 'draft',
        source_name: '',
        source_url: '',
        source_notes: '',
        admin_notes: '',
      });

      // Refetch corporate actions list
      fetchCorporateActions();
    } catch (err) {
      setCreateError(err.message || 'Failed to create corporate action');
    } finally {
      setCreateLoading(false);
    }
  };

  const trackingRequestConfig = (kind) => {
    if (kind === 'retire') {
      return {
        endpoint: '/admin/data-quality/ticker-tracking/retire',
        payload: { ticker: trackingRetireForm.ticker.trim().toUpperCase(), admin_notes: trackingRetireForm.admin_notes.trim() },
      };
    }
    if (kind === 'remove') {
      return {
        endpoint: '/admin/data-quality/ticker-memberships',
        payload: {
          ticker: trackingMembershipForm.ticker.trim().toUpperCase(),
          add_index_codes: [],
          remove_index_codes: [trackingMembershipForm.index_code],
          admin_notes: trackingMembershipForm.admin_notes.trim(),
        },
      };
    }
    const payload = { ...trackingAddForm, ticker: trackingAddForm.ticker.trim().toUpperCase(), index_code: trackingAddForm.index_code, admin_notes: trackingAddForm.admin_notes.trim() };
    Object.keys(payload).forEach((key) => {
      if (payload[key] === '' || payload[key] === null || payload[key] === undefined) delete payload[key];
    });
    if (payload.priority !== undefined) payload.priority = Number(payload.priority);
    return { endpoint: '/admin/data-quality/ticker-memberships/add', payload };
  };

  const handleTrackingPreview = async (kind) => {
    const config = trackingRequestConfig(kind);
    setTrackingLoading(prev => ({ ...prev, [kind]: true }));
    setTrackingErrors(prev => ({ ...prev, [kind]: '' }));
    setTrackingSuccess(prev => ({ ...prev, [kind]: '' }));
    try {
      const response = await apiFetch(`${API_BASE_URL}${config.endpoint}/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(config.payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to preview ticker change');
      setTrackingPreviews(prev => ({ ...prev, [kind]: data }));
    } catch (err) {
      setTrackingPreviews(prev => ({ ...prev, [kind]: null }));
      setTrackingErrors(prev => ({ ...prev, [kind]: err.message || 'Unable to preview ticker change' }));
    } finally {
      setTrackingLoading(prev => ({ ...prev, [kind]: false }));
    }
  };

  const handleTrackingApply = async (kind) => {
    if (!trackingPreviews[kind]) {
      setTrackingErrors(prev => ({ ...prev, [kind]: 'Preview the change before applying it' }));
      return;
    }
    if (!window.confirm('Confirm this current ticker-tracking change. Holdings and historical data are preserved.')) return;
    const config = trackingRequestConfig(kind);
    setTrackingApplyLoading(prev => ({ ...prev, [kind]: true }));
    setTrackingErrors(prev => ({ ...prev, [kind]: '' }));
    setTrackingSuccess(prev => ({ ...prev, [kind]: '' }));
    try {
      const response = await apiFetch(`${API_BASE_URL}${config.endpoint}/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(config.payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Unable to apply ticker change');
      setTrackingPreviews(prev => ({ ...prev, [kind]: data }));
      setTrackingSuccess(prev => ({ ...prev, [kind]: `Applied. Audit event ${data.audit_event_id}.` }));
      fetchCorporateActions();
    } catch (err) {
      setTrackingErrors(prev => ({ ...prev, [kind]: err.message || 'Unable to apply ticker change' }));
    } finally {
      setTrackingApplyLoading(prev => ({ ...prev, [kind]: false }));
    }
  };

  const renderTrackingPreview = (kind) => {
    const preview = trackingPreviews[kind];
    if (!preview) return null;
    return (
      <div className="admin-quality-dryrun-panel" style={{ marginTop: '1rem' }}>
        <p><strong>Ticker:</strong> {preview.ticker}</p>
        <p><strong>Active memberships:</strong> {preview.active_memberships?.join(', ') || 'None'}</p>
        {preview.index_code && <p><strong>Selected index:</strong> {preview.index_code}</p>}
        {preview.provider_symbol && <p><strong>Provider symbol:</strong> {preview.provider_symbol} (validated structurally)</p>}
        <p><strong>Affected holdings:</strong> {preview.affected_holdings_count} across {preview.affected_portfolios_count} portfolios and {preview.affected_users_count} users</p>
        <p><strong>Changes:</strong> {preview.changes?.map(change => `${change.action}${change.index_code ? ` (${change.index_code})` : ''}`).join(', ') || 'None'}</p>
        <button type="button" className="admin-quality-btn-primary" onClick={() => handleTrackingApply(kind)} disabled={trackingApplyLoading[kind]}>
          {trackingApplyLoading[kind] ? 'Applying...' : 'Apply Change'}
        </button>
      </div>
    );
  };

  const issueTypeLabels = {
    missing_price: 'Missing Price Data',
    stale_price: 'Stale Price Data',
    missing_fundamentals: 'Missing Fundamentals',
    corporate_action: 'Corporate Action Review',
  };

  const formatPrice = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? `$${numberValue.toFixed(2)}` : 'N/A';
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') {
      return 'N/A';
    }
    return value;
  };

  if (loading) {
    return (
      <div className="admin-quality-page authenticated-page">
        <div className="admin-quality-container authenticated-page-content">
          <div className="admin-quality-header">
            <h1 className="admin-quality-title">Admin / Data Quality</h1>
          </div>
          <p className="admin-quality-loading">Loading data quality issues...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-quality-page authenticated-page">
        <div className="admin-quality-container authenticated-page-content">
          <div className="admin-quality-header">
            <h1 className="admin-quality-title">Admin / Data Quality</h1>
          </div>
          <div className="admin-quality-error">{error}</div>
        </div>
      </div>
    );
  }

  const handleDryRunClick = async (actionId) => {
    // Toggle if already expanded
    if (expandedDryRun === actionId) {
      setExpandedDryRun(null);
      return;
    }

    // Use cached data if available
    if (dryRunCache[actionId]) {
      setExpandedDryRun(actionId);
      return;
    }

    // Fetch dry-run preview
    setDryRunLoading(prev => ({ ...prev, [actionId]: true }));
    setDryRunError(prev => ({ ...prev, [actionId]: '' }));

    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/corporate-actions/${actionId}/dry-run`, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch dry-run preview');
      }

      const data = await response.json();
      setDryRunCache(prev => ({ ...prev, [actionId]: data }));
      setExpandedDryRun(actionId);
    } catch (err) {
      setDryRunError(prev => ({ ...prev, [actionId]: err.message || 'Failed to load dry-run preview' }));
    } finally {
      setDryRunLoading(prev => ({ ...prev, [actionId]: false }));
    }
  };

  const handleApplyAction = async (actionId) => {
    const corporateAction = dryRunCache[actionId]?.corporate_action;
    const confirmed = window.confirm(
      `Apply corporate action ${corporateAction?.old_ticker || ''} to ${corporateAction?.new_ticker || ''}? This will update affected portfolio holdings.`
    );

    if (!confirmed) {
      return;
    }

    setApplyLoading(prev => ({ ...prev, [actionId]: true }));
    setApplyError(prev => ({ ...prev, [actionId]: '' }));
    setApplySuccess(prev => ({ ...prev, [actionId]: '' }));

    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/corporate-actions/${actionId}/apply`, {
        method: 'POST',
        headers: {
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to apply corporate action');
      }

      const data = await response.json();
      setApplyResult(prev => ({ ...prev, [actionId]: data }));
      setApplySuccess(prev => ({ ...prev, [actionId]: 'Corporate action applied successfully' }));
      
      // Refresh corporate actions list to update status
      fetchCorporateActions();
      
      // Update dry-run cache to reflect applied status so reapply is prevented
      setDryRunCache(prev => {
        if (prev[actionId]) {
          return {
            ...prev,
            [actionId]: {
              ...prev[actionId],
              corporate_action: {
                ...prev[actionId].corporate_action,
                status: 'applied'
              }
            }
          };
        }
        return prev;
      });
    } catch (err) {
      setApplyError(prev => ({ ...prev, [actionId]: err.message || 'Failed to apply corporate action' }));
    } finally {
      setApplyLoading(prev => ({ ...prev, [actionId]: false }));
    }
  };

  const resetManualPreviewState = () => {
    setManualPreview(null);
    setManualSuccess('');
    setManualError('');
  };

  const buildManualAdjustmentPayload = () => {
    const payload = {
      holding_id: Number(manualSelectedHolding?.holding_id),
      admin_notes: manualFormData.admin_notes.trim(),
    };

    const quantity = manualFormData.quantity === '' ? null : Number(manualFormData.quantity);
    const currentQuantity = manualSelectedHolding?.quantity ?? null;
    if (quantity !== currentQuantity) {
      payload.quantity = quantity;
    }

    const averageBuyPrice = manualFormData.average_buy_price === '' ? null : Number(manualFormData.average_buy_price);
    const currentAverageBuyPrice = manualSelectedHolding?.average_buy_price ?? null;
    if (averageBuyPrice !== currentAverageBuyPrice) {
      payload.average_buy_price = averageBuyPrice;
    }

    const ticker = manualFormData.ticker.trim().toUpperCase();
    if (ticker !== (manualSelectedHolding?.ticker || '').trim().toUpperCase()) {
      payload.ticker = ticker;
    }

    const isActive = manualFormData.is_active === 'true';
    if (isActive !== Boolean(manualSelectedHolding?.is_active)) {
      payload.is_active = isActive;
    }

    return payload;
  };

  const hasManualAdjustmentChanges = (payload) => (
    Object.prototype.hasOwnProperty.call(payload, 'quantity')
    || Object.prototype.hasOwnProperty.call(payload, 'average_buy_price')
    || Object.prototype.hasOwnProperty.call(payload, 'ticker')
    || Object.prototype.hasOwnProperty.call(payload, 'is_active')
  );

  const selectManualHolding = (holding) => {
    setManualSelectedHolding(holding);
    setManualFormData({
      quantity: holding.quantity !== null && holding.quantity !== undefined ? String(holding.quantity) : '',
      average_buy_price: holding.average_buy_price !== null && holding.average_buy_price !== undefined ? String(holding.average_buy_price) : '',
      ticker: holding.ticker || '',
      is_active: holding.is_active ? 'true' : 'false',
      admin_notes: '',
    });
    setManualDeleteConfirmation('');
    resetManualPreviewState();
  };

  const handleManualSearch = async () => {
    setManualLoading(true);
    setManualError('');
    setManualSuccess('');
    setManualPreview(null);
    try {
      const params = new URLSearchParams();
      if (manualSearch.holding_id.trim()) {
        params.set('holding_id', manualSearch.holding_id.trim());
      }
      if (manualSearch.ticker.trim()) {
        params.set('ticker', manualSearch.ticker.trim().toUpperCase());
      }

      const response = await apiFetch(`${API_BASE_URL}/admin/manual-holding-adjustments/holdings?${params.toString()}`, { method: 'GET' });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to search holdings');
      }

      const data = await response.json();
      const holdings = data.holdings || [];
      setManualHoldings(holdings);
      if (holdings.length === 1) {
        selectManualHolding(holdings[0]);
      } else {
        setManualSelectedHolding(null);
      }
    } catch (err) {
      setManualError(err.message || 'Failed to search holdings');
    } finally {
      setManualLoading(false);
    }
  };

  const handleManualPreview = async () => {
    if (!manualSelectedHolding) {
      setManualError('Select a holding before previewing an adjustment');
      return;
    }

    setManualPreviewLoading(true);
    setManualError('');
    setManualSuccess('');
    try {
      const payload = buildManualAdjustmentPayload();
      if (!hasManualAdjustmentChanges(payload)) {
        throw new Error('Change quantity, average buy price, ticker, or active status before previewing an adjustment');
      }

      const response = await apiFetch(`${API_BASE_URL}/admin/manual-holding-adjustments/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to preview manual adjustment');
      }

      const data = await response.json();
      setManualPreview(data);
    } catch (err) {
      setManualPreview(null);
      setManualError(err.message || 'Failed to preview manual adjustment');
    } finally {
      setManualPreviewLoading(false);
    }
  };

  const handleManualApply = async () => {
    if (!manualPreview) {
      setManualError('Preview the manual adjustment before applying it');
      return;
    }

    setManualApplyLoading(true);
    setManualError('');
    setManualSuccess('');
    try {
      const payload = buildManualAdjustmentPayload();
      if (!hasManualAdjustmentChanges(payload)) {
        throw new Error('Change quantity, average buy price, ticker, or active status before applying an adjustment');
      }

      const response = await apiFetch(`${API_BASE_URL}/admin/manual-holding-adjustments/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to apply manual adjustment');
      }

      const data = await response.json();
      setManualPreview(data);
      setManualSuccess(`Manual adjustment applied. Audit action ${data.audit_action_id}.`);
      setManualSelectedHolding(prev => prev ? {
        ...prev,
        ticker: data.after.ticker,
        quantity: data.after.quantity,
        average_buy_price: data.after.average_buy_price,
        is_active: data.after.is_active,
        removed_at: data.after.removed_at,
        updated_at: data.after.updated_at,
        hard_delete_allowed: data.hard_delete_allowed,
      } : prev);
      fetchCorporateActions();
    } catch (err) {
      setManualError(err.message || 'Failed to apply manual adjustment');
    } finally {
      setManualApplyLoading(false);
    }
  };

  const handleManualHardDelete = async () => {
    if (!manualSelectedHolding) {
      setManualError('Select a holding before hard deleting it');
      return;
    }

    setManualDeleteLoading(true);
    setManualError('');
    setManualSuccess('');
    try {
      const response = await apiFetch(`${API_BASE_URL}/admin/manual-holding-adjustments/hard-delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          holding_id: Number(manualSelectedHolding.holding_id),
          admin_notes: manualFormData.admin_notes.trim(),
          confirmation: manualDeleteConfirmation,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to hard delete holding');
      }

      const data = await response.json();
      setManualSuccess(`Holding ${data.holding_id} hard deleted. Audit action ${data.audit_action_id}.`);
      setManualPreview(null);
      setManualSelectedHolding(null);
      setManualDeleteConfirmation('');
      setManualHoldings(prev => prev.filter(holding => holding.holding_id !== data.holding_id));
      fetchCorporateActions();
    } catch (err) {
      setManualError(err.message || 'Failed to hard delete holding');
    } finally {
      setManualDeleteLoading(false);
    }
  };

  const actionTypeLabels = {
    ticker_change: 'Ticker Change',
    stock_for_stock_merger: 'Stock-for-Stock Merger',
    split: 'Stock Split',
    reverse_split: 'Reverse Split',
    spin_off: 'Spin-Off',
    delisting: 'Delisting',
    manual_adjustment: 'Manual Adjustment',
  };

  const applySupportedActionTypes = [
    'ticker_change',
    'stock_for_stock_merger',
    'split',
    'reverse_split',
    'spin_off',
    'delisting',
  ];

  const createActionRule = CREATE_ACTION_RULES[createFormData.action_type];
  const hidesNewTickerCreate = createActionRule ? !createActionRule.showNewTicker : ['delisting', 'split', 'reverse_split'].includes(createFormData.action_type);
  const hidesConversionRatioCreate = createActionRule ? !createActionRule.showConversionRatio : ['delisting', 'ticker_change'].includes(createFormData.action_type);

  return (
    <div className="admin-quality-page authenticated-page">
      <div className="admin-quality-container authenticated-page-content">
        <div className="admin-quality-header">
          <h1 className="admin-quality-title">Admin / Data Quality</h1>
          <p className="admin-quality-subtitle">Read-only review of ticker, price, fundamentals, and possible corporate-action issues.</p>
        </div>

        <div className="admin-quality-metrics">
          <div className="admin-quality-metric-card">
            <h3>Open Issues</h3>
            <div className="admin-quality-metric-value">{summary.total_issues}</div>
          </div>

          <div className="admin-quality-metric-card">
            <h3>Affected Users</h3>
            <div className="admin-quality-metric-value">{summary.affected_users_total}</div>
          </div>

          <div className="admin-quality-metric-card">
            <h3>Affected Portfolios</h3>
            <div className="admin-quality-metric-value">{summary.affected_portfolios_total}</div>
          </div>

          <div className="admin-quality-metric-card">
            <h3>Affected Holdings</h3>
            <div className="admin-quality-metric-value">{summary.affected_holdings_total}</div>
          </div>
        </div>

        <div className="admin-quality-metrics admin-quality-snapshot-metrics" aria-label="Current data snapshots">
          <div className="admin-quality-metric-card">
            <h3>Market Data Updated</h3>
            <p className="admin-quality-card-detail"><strong>Last successful price write:</strong> {formatAdminTimestamp(snapshotCards.market_data.price_updated_at)}</p>
            <p className="admin-quality-card-detail"><strong>Last successful current-fundamentals write:</strong> {formatAdminTimestamp(snapshotCards.market_data.current_fundamentals_updated_at)}</p>
            <p className="admin-quality-card-detail"><strong>Last successful quarterly-fundamentals write:</strong> {formatAdminTimestamp(snapshotCards.market_data.quarterly_fundamentals_updated_at)}</p>
            <p className="admin-quality-card-note">{snapshotCards.market_data.coverage_status}</p>
          </div>

          <div className="admin-quality-metric-card">
            <h3>Stock Scanner Snapshot</h3>
            <p className="admin-quality-card-detail"><strong>Active snapshot:</strong> {formatAdminTimestamp(snapshotCards.scanner_snapshot.current_snapshot_at)}</p>
          </div>

          <div className="admin-quality-metric-card">
            <h3>Strategy Lab Snapshot</h3>
            <p className="admin-quality-card-detail"><strong>Active publication:</strong> {formatAdminTimestamp(snapshotCards.strategy_lab_snapshot.publication_created_at)}</p>
            <p className="admin-quality-card-detail"><strong>Historical anchor:</strong> {formatHistoricalAnchorDate(snapshotCards.strategy_lab_snapshot.anchor_date)}</p>
          </div>
        </div>

        <div className="admin-quality-section">
          <div className="admin-quality-section-header">
            <h2>Data Quality Issues</h2>
          </div>

          {issues.length === 0 ? (
            <p className="admin-quality-empty">No open data quality issues found.</p>
          ) : (
            <div className="admin-quality-table-wrapper">
              <table className="admin-quality-table">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th>Issue</th>
                  <th>Index Membership</th>
                  <th>Affected Users</th>
                  <th>Portfolios</th>
                  <th>Holdings</th>
                  <th>Status</th>
                  <th>Review</th>
                </tr>
              </thead>
              <tbody>
                {issues.map((issue) => (
                  <React.Fragment key={`${issue.ticker}-${issue.issue_type}`}>
                    <tr>
                      <td>{issue.ticker}</td>
                      <td>{issueTypeLabels[issue.issue_type] || issue.issue_type}</td>
                      <td style={{ whiteSpace: 'normal', minWidth: '10rem' }}>
                        {issue.active_index_memberships?.length ? issue.active_index_memberships.join(', ') : 'None'}
                      </td>
                      <td>{issue.affected_users}</td>
                      <td>{issue.affected_portfolios}</td>
                      <td>{issue.affected_holdings}</td>
                      <td>{issue.status || 'open'}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-quality-btn-secondary"
                          onClick={() => handleReviewClick(issue)}
                        >
                          {expandedIssue?.ticker === issue.ticker && expandedIssue?.issue_type === issue.issue_type ? 'Collapse' : 'Review'}
                        </button>
                      </td>
                    </tr>
                    {expandedIssue?.ticker === issue.ticker && expandedIssue?.issue_type === issue.issue_type && (
                      <tr>
                        <td colSpan="9" style={{ padding: 0 }}>
                          <div className="admin-quality-review-panel">
                            <div className="admin-quality-review-header">
                              <h3>
                                Review: {issue.ticker} - {issueTypeLabels[issue.issue_type] || issue.issue_type}
                              </h3>
                            </div>

                            {detailLoading ? (
                              <p className="admin-quality-loading">Loading ticker detail...</p>
                            ) : detailError ? (
                              <p className="admin-quality-error">Error: {detailError}</p>
                            ) : detailCache[issue.ticker] ? (
                              <>
                                <div className="admin-quality-review-grid">
                                  <div>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Company:</strong> {formatValue(detailCache[issue.ticker].company_name)}</p>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Ticker Active:</strong> {detailCache[issue.ticker].ticker_active ? 'Yes' : 'No'}</p>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Has Fundamentals:</strong> {detailCache[issue.ticker].has_fundamentals ? 'Yes' : 'No'}</p>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Latest Price:</strong> {formatPrice(detailCache[issue.ticker].latest_price)}</p>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Latest Price Date:</strong> {formatValue(detailCache[issue.ticker].latest_price_date)}</p>
                                  </div>
                                  <div>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Affected Users:</strong> {detailCache[issue.ticker].affected_users_count}</p>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Affected Portfolios:</strong> {detailCache[issue.ticker].affected_portfolios_count}</p>
                                    <p style={{ margin: '0.25rem 0' }}><strong>Affected Holdings:</strong> {detailCache[issue.ticker].affected_holdings_count}</p>
                                  </div>
                                </div>

                                {detailCache[issue.ticker].holdings && detailCache[issue.ticker].holdings.length > 0 && (
                                  <div className="admin-quality-holdings-section">
                                    <h4>Holdings</h4>
                                    <div style={{ overflowX: 'auto' }}>
                                      <table className="admin-quality-holdings-table">
                                        <thead>
                                          <tr>
                                            <th>User Email</th>
                                            <th>Portfolio</th>
                                            <th style={{ textAlign: 'right' }}>Quantity</th>
                                            <th style={{ textAlign: 'right' }}>Avg Buy Price</th>
                                            <th style={{ textAlign: 'right' }}>Est. Market Value</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {detailCache[issue.ticker].holdings.map((holding, idx) => (
                                            <tr key={idx}>
                                              <td>{formatValue(holding.user_email)}</td>
                                              <td>{formatValue(holding.portfolio_name)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatValue(holding.quantity)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.average_buy_price)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.estimated_market_value)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

        {/* Current ticker-tracking controls */}
        <div className="admin-quality-section">
          <div className="admin-quality-section-header">
            <h2>Ticker Tracking</h2>
            <p className="admin-quality-card-note">These admin actions change current scanner membership only. Holdings and historical records are preserved.</p>
          </div>
          <div className="admin-quality-form-grid">
            <div className="admin-quality-form-card">
              <h3>Retire from Scanner</h3>
              <input className="admin-quality-input" placeholder="Ticker" maxLength={20} value={trackingRetireForm.ticker} onChange={e => setTrackingRetireForm(prev => ({ ...prev, ticker: e.target.value.toUpperCase() }))} />
              <textarea className="admin-quality-textarea" placeholder="Admin notes (required)" rows={2} value={trackingRetireForm.admin_notes} onChange={e => setTrackingRetireForm(prev => ({ ...prev, admin_notes: e.target.value }))} />
              <button type="button" className="admin-quality-btn-secondary" onClick={() => handleTrackingPreview('retire')} disabled={trackingLoading.retire}>{trackingLoading.retire ? 'Previewing...' : 'Preview Retirement'}</button>
              {trackingErrors.retire && <div className="admin-quality-error">{trackingErrors.retire}</div>}
              {trackingSuccess.retire && <div className="admin-quality-success">{trackingSuccess.retire}</div>}
              {renderTrackingPreview('retire')}
            </div>
            <div className="admin-quality-form-card">
              <h3>Remove Index Membership</h3>
              <input className="admin-quality-input" placeholder="Ticker" maxLength={20} value={trackingMembershipForm.ticker} onChange={e => setTrackingMembershipForm(prev => ({ ...prev, ticker: e.target.value.toUpperCase() }))} />
              <select className="admin-quality-select" value={trackingMembershipForm.index_code} onChange={e => setTrackingMembershipForm(prev => ({ ...prev, index_code: e.target.value }))}>{SUPPORTED_INDEX_OPTIONS.map(option => <option key={option.code} value={option.code}>{option.label}</option>)}</select>
              <textarea className="admin-quality-textarea" placeholder="Admin notes (required)" rows={2} value={trackingMembershipForm.admin_notes} onChange={e => setTrackingMembershipForm(prev => ({ ...prev, admin_notes: e.target.value }))} />
              <button type="button" className="admin-quality-btn-secondary" onClick={() => handleTrackingPreview('remove')} disabled={trackingLoading.remove}>{trackingLoading.remove ? 'Previewing...' : 'Preview Removal'}</button>
              {trackingErrors.remove && <div className="admin-quality-error">{trackingErrors.remove}</div>}
              {trackingSuccess.remove && <div className="admin-quality-success">{trackingSuccess.remove}</div>}
              {renderTrackingPreview('remove')}
            </div>
            <div className="admin-quality-form-card">
              <h3>Add Index Member</h3>
              <input className="admin-quality-input" placeholder="Canonical ticker" maxLength={20} value={trackingAddForm.ticker} onChange={e => setTrackingAddForm(prev => ({ ...prev, ticker: e.target.value.toUpperCase() }))} />
              <select className="admin-quality-select" value={trackingAddForm.index_code} onChange={e => setTrackingAddForm(prev => ({ ...prev, index_code: e.target.value }))}>{SUPPORTED_INDEX_OPTIONS.map(option => <option key={option.code} value={option.code}>{option.label}</option>)}</select>
              <input className="admin-quality-input" placeholder="Company name" value={trackingAddForm.company_name} onChange={e => setTrackingAddForm(prev => ({ ...prev, company_name: e.target.value }))} />
              <input className="admin-quality-input" placeholder="Exchange (optional)" value={trackingAddForm.exchange} onChange={e => setTrackingAddForm(prev => ({ ...prev, exchange: e.target.value }))} />
              <input className="admin-quality-input" placeholder="Country (optional)" value={trackingAddForm.country} onChange={e => setTrackingAddForm(prev => ({ ...prev, country: e.target.value }))} />
              <input className="admin-quality-input" placeholder="Currency (optional)" value={trackingAddForm.currency} onChange={e => setTrackingAddForm(prev => ({ ...prev, currency: e.target.value.toUpperCase() }))} />
              <input className="admin-quality-input" placeholder="Asset type (optional)" value={trackingAddForm.asset_type} onChange={e => setTrackingAddForm(prev => ({ ...prev, asset_type: e.target.value }))} />
              <input className="admin-quality-input" placeholder="Provider symbol (optional)" maxLength={50} value={trackingAddForm.data_source_symbol} onChange={e => setTrackingAddForm(prev => ({ ...prev, data_source_symbol: e.target.value.toUpperCase() }))} />
              <input className="admin-quality-input" type="number" placeholder="Priority (optional)" value={trackingAddForm.priority} onChange={e => setTrackingAddForm(prev => ({ ...prev, priority: e.target.value }))} />
              <textarea className="admin-quality-textarea" placeholder="Source note (optional)" rows={2} value={trackingAddForm.source_note} onChange={e => setTrackingAddForm(prev => ({ ...prev, source_note: e.target.value }))} />
              <textarea className="admin-quality-textarea" placeholder="Admin notes (required)" rows={2} value={trackingAddForm.admin_notes} onChange={e => setTrackingAddForm(prev => ({ ...prev, admin_notes: e.target.value }))} />
              <button type="button" className="admin-quality-btn-secondary" onClick={() => handleTrackingPreview('add')} disabled={trackingLoading.add}>{trackingLoading.add ? 'Previewing...' : 'Preview Add'}</button>
              {trackingErrors.add && <div className="admin-quality-error">{trackingErrors.add}</div>}
              {trackingSuccess.add && <div className="admin-quality-success">{trackingSuccess.add}</div>}
              {renderTrackingPreview('add')}
            </div>
          </div>
        </div>

        {/* Manual Adjustment Section */}
        <div className="admin-quality-section">
          <div className="admin-quality-section-header">
            <h2>Manual Holding Adjustment</h2>
            <p className="admin-quality-card-note">Use this tool to correct one user’s portfolio holding, such as its ticker, quantity, average price, or active status. It does not change scanner tracking or historical data.</p>
          </div>

          <div className="admin-quality-form-card">
            {manualSuccess && (
              <div className="admin-quality-success">
                {manualSuccess}
              </div>
            )}

            {manualError && (
              <div className="admin-quality-error">
                {manualError}
              </div>
            )}

            <div className="admin-quality-form-grid">
              <div className="admin-quality-field">
                <label className="admin-quality-label">
                  Holding ID
                </label>
                <input
                  type="number"
                  value={manualSearch.holding_id}
                  onChange={(e) => setManualSearch(prev => ({ ...prev, holding_id: e.target.value }))}
                  placeholder="123"
                  className="admin-quality-input"
                />
              </div>

              <div className="admin-quality-field">
                <label className="admin-quality-label">
                  Ticker
                </label>
                <input
                  type="text"
                  value={manualSearch.ticker}
                  onChange={(e) => setManualSearch(prev => ({ ...prev, ticker: e.target.value.toUpperCase() }))}
                  placeholder="AAPL"
                  className="admin-quality-input"
                />
              </div>

              <div className="admin-quality-field">
                <label className="admin-quality-label">
                  Search
                </label>
                <button
                  type="button"
                  onClick={handleManualSearch}
                  disabled={manualLoading}
                  className="admin-quality-btn-secondary"
                >
                  {manualLoading ? 'Searching...' : 'Load Holdings'}
                </button>
              </div>
            </div>

            {manualHoldings.length > 0 && (
              <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
                <table className="admin-quality-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Portfolio</th>
                      <th>Ticker</th>
                      <th style={{ textAlign: 'right' }}>Quantity</th>
                      <th style={{ textAlign: 'right' }}>Avg Price</th>
                      <th>Active</th>
                      <th>Select</th>
                    </tr>
                  </thead>
                  <tbody>
                    {manualHoldings.map((holding) => (
                      <tr key={holding.holding_id}>
                        <td>{holding.holding_id}</td>
                        <td>{holding.user_email}</td>
                        <td>{holding.portfolio_name}</td>
                        <td>{holding.ticker}</td>
                        <td style={{ textAlign: 'right' }}>{formatValue(holding.quantity)}</td>
                        <td style={{ textAlign: 'right' }}>{formatPrice(holding.average_buy_price)}</td>
                        <td>{holding.is_active ? 'Yes' : 'No'}</td>
                        <td>
                          <button
                            type="button"
                            className="admin-quality-btn-secondary"
                            onClick={() => selectManualHolding(holding)}
                          >
                            Select
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {manualSelectedHolding && (
              <div className="admin-quality-dryrun-panel" style={{ marginTop: '1rem' }}>
                <div className="admin-quality-dryrun-header">
                  <h3>Adjust Holding {manualSelectedHolding.holding_id}</h3>
                  <p className="admin-quality-dryrun-subtitle">
                    {manualSelectedHolding.user_email} / {manualSelectedHolding.portfolio_name}
                  </p>
                </div>

                <div className="admin-quality-form-grid">
                  <div className="admin-quality-field">
                    <label className="admin-quality-label">
                      Quantity
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      value={manualFormData.quantity}
                      onChange={(e) => {
                        setManualFormData(prev => ({ ...prev, quantity: e.target.value }));
                        resetManualPreviewState();
                      }}
                      className="admin-quality-input"
                    />
                  </div>

                  <div className="admin-quality-field">
                    <label className="admin-quality-label">
                      Average Buy Price
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      value={manualFormData.average_buy_price}
                      onChange={(e) => {
                        setManualFormData(prev => ({ ...prev, average_buy_price: e.target.value }));
                        resetManualPreviewState();
                      }}
                      className="admin-quality-input"
                    />
                  </div>

                  <div className="admin-quality-field">
                    <label className="admin-quality-label">
                      Ticker
                    </label>
                    <input
                      type="text"
                      value={manualFormData.ticker}
                      onChange={(e) => {
                        setManualFormData(prev => ({ ...prev, ticker: e.target.value.toUpperCase() }));
                        resetManualPreviewState();
                      }}
                      maxLength={20}
                      className="admin-quality-input"
                    />
                  </div>

                  <div className="admin-quality-field">
                    <label className="admin-quality-label">
                      Active
                    </label>
                    <select
                      value={manualFormData.is_active}
                      onChange={(e) => {
                        setManualFormData(prev => ({ ...prev, is_active: e.target.value }));
                        resetManualPreviewState();
                      }}
                      className="admin-quality-select"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                    </select>
                  </div>

                  <div className="admin-quality-field admin-quality-field-full">
                    <label className="admin-quality-label">
                      Admin Notes *
                    </label>
                    <textarea
                      value={manualFormData.admin_notes}
                      onChange={(e) => {
                        setManualFormData(prev => ({ ...prev, admin_notes: e.target.value }));
                        resetManualPreviewState();
                      }}
                      rows={2}
                      placeholder="Reason for this one-holding correction..."
                      className="admin-quality-textarea"
                    />
                  </div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <button
                    type="button"
                    onClick={handleManualPreview}
                    disabled={manualPreviewLoading}
                    className="admin-quality-btn-secondary"
                  >
                    {manualPreviewLoading ? 'Previewing...' : 'Preview Manual Adjustment'}
                  </button>
                </div>

                {manualPreview && (
                  <div className="admin-quality-dryrun-result" style={{ marginTop: '1rem' }}>
                    <h4>Before / After</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table className="admin-quality-dryrun-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Ticker</th>
                            <th style={{ textAlign: 'right' }}>Quantity</th>
                            <th style={{ textAlign: 'right' }}>Avg Price</th>
                            <th>Active</th>
                            <th>Removed At</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Before</td>
                            <td>{manualPreview.before.ticker}</td>
                            <td style={{ textAlign: 'right' }}>{formatValue(manualPreview.before.quantity)}</td>
                            <td style={{ textAlign: 'right' }}>{formatPrice(manualPreview.before.average_buy_price)}</td>
                            <td>{manualPreview.before.is_active ? 'Yes' : 'No'}</td>
                            <td>{manualPreview.before.removed_at || '-'}</td>
                          </tr>
                          <tr>
                            <td>After</td>
                            <td>{manualPreview.after.ticker}</td>
                            <td style={{ textAlign: 'right' }}>{formatValue(manualPreview.after.quantity)}</td>
                            <td style={{ textAlign: 'right' }}>{formatPrice(manualPreview.after.average_buy_price)}</td>
                            <td>{manualPreview.after.is_active ? 'Yes' : 'No'}</td>
                            <td>{manualPreview.after.removed_at || '-'}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <div style={{ marginTop: '1rem' }}>
                      <button
                        type="button"
                        onClick={handleManualApply}
                        disabled={manualApplyLoading}
                        className="admin-quality-btn-primary"
                      >
                        {manualApplyLoading ? 'Applying...' : 'Apply Manual Adjustment'}
                      </button>
                    </div>
                  </div>
                )}

                <div className="admin-quality-dryrun-warnings" style={{ marginTop: '1rem' }}>
                  <h4>Hard Delete</h4>
                  <p style={{ margin: '0.25rem 0' }}>
                    Allowed: {manualSelectedHolding.hard_delete_allowed ? 'Yes' : 'No'}
                    {manualSelectedHolding.is_synthetic_test_data ? ' (synthetic/test holding)' : ''}
                  </p>
                  <div className="admin-quality-form-grid">
                    <div className="admin-quality-field">
                      <label className="admin-quality-label">
                        Confirmation
                      </label>
                      <input
                        type="text"
                        value={manualDeleteConfirmation}
                        onChange={(e) => setManualDeleteConfirmation(e.target.value)}
                        placeholder="DELETE HOLDING"
                        className="admin-quality-input"
                      />
                    </div>
                    <div className="admin-quality-field">
                      <label className="admin-quality-label">
                        Delete
                      </label>
                      <button
                        type="button"
                        onClick={handleManualHardDelete}
                        disabled={
                          manualDeleteLoading
                          || !manualSelectedHolding.hard_delete_allowed
                          || manualDeleteConfirmation !== 'DELETE HOLDING'
                        }
                        className="admin-quality-btn-secondary"
                      >
                        {manualDeleteLoading ? 'Deleting...' : 'Hard Delete Holding'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Corporate Actions Section */}
        <div className="admin-quality-section">
          <div className="admin-quality-section-header">
            <h2>Corporate Actions — Update Portfolio Holdings</h2>
            <p className="admin-quality-card-note">These actions update users’ portfolio holdings after company events. They do not retire tickers, change index membership, or rewrite historical market data.</p>
          </div>

          {/* Create Form */}
          <div className="admin-quality-form-card">
            <h3>Create Corporate Action</h3>
            
            {createSuccess && (
              <div className="admin-quality-success">
                {createSuccess}
              </div>
            )}

            {createError && (
              <div className="admin-quality-error">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateSubmit}>
              <div className="admin-quality-form-grid">
                <div className="admin-quality-field">
                  <label className="admin-quality-label">
                    Action Type *
                  </label>
                  <select
                    value={createFormData.action_type}
                    onChange={(e) => handleCreateFormChange('action_type', e.target.value)}
                    required
                    className="admin-quality-select"
                  >
                  <option value="ticker_change">Ticker Change</option>
                  <option value="stock_for_stock_merger">Stock-for-Stock Merger</option>
                  <option value="split">Stock Split</option>
                  <option value="reverse_split">Reverse Split</option>
                  <option value="spin_off">Spin-Off</option>
                  <option value="delisting">Delisting</option>
                  </select>
                  <p className="admin-quality-card-note">{CORPORATE_ACTION_EXPLANATIONS[createFormData.action_type]}</p>
                </div>

                <div className="admin-quality-field">
                  <label className="admin-quality-label">
                    Old Ticker *
                  </label>
                  <input
                    type="text"
                    value={createFormData.old_ticker}
                    onChange={(e) => handleCreateFormChange('old_ticker', e.target.value.toUpperCase())}
                    required
                    maxLength={20}
                    placeholder="AAPL"
                    className="admin-quality-input"
                  />
                </div>

                {!hidesNewTickerCreate && (
                  <div className="admin-quality-field">
                    <label className="admin-quality-label">
                      New Ticker
                    </label>
                    <input
                      type="text"
                      value={createFormData.new_ticker}
                      onChange={(e) => handleCreateFormChange('new_ticker', e.target.value.toUpperCase())}
                      maxLength={20}
                      placeholder="AAPL2"
                      className="admin-quality-input"
                    />
                  </div>
                )}

                <div className="admin-quality-field">
                  <label className="admin-quality-label">
                    Effective Date *
                  </label>
                  <input
                    type="date"
                    value={createFormData.effective_date}
                    onChange={(e) => handleCreateFormChange('effective_date', e.target.value)}
                    required
                    className="admin-quality-input"
                  />
                </div>

                <div className="admin-quality-field">
                  <label className="admin-quality-label">
                    Announcement Date
                  </label>
                  <input
                    type="date"
                    value={createFormData.announcement_date}
                    onChange={(e) => handleCreateFormChange('announcement_date', e.target.value)}
                    className="admin-quality-input"
                  />
                </div>

                {!hidesConversionRatioCreate && (
                  <div className="admin-quality-field">
                    <label className="admin-quality-label">
                      Conversion Ratio
                    </label>
                    <input
                      type="number"
                      step="0.0001"
                      value={createFormData.conversion_ratio}
                      onChange={(e) => handleCreateFormChange('conversion_ratio', e.target.value)}
                      placeholder="1.5"
                      className="admin-quality-input"
                    />
                  </div>
                )}

                <div className="admin-quality-field">
                  <label className="admin-quality-label">
                    Status *
                  </label>
                  <select
                    value={createFormData.status}
                    onChange={(e) => handleCreateFormChange('status', e.target.value)}
                    required
                    className="admin-quality-select"
                  >
                    <option value="draft">Draft</option>
                    <option value="review_ready">Review Ready</option>
                  </select>
                </div>

                <div className="admin-quality-field">
                  <label className="admin-quality-label">
                    Source Name
                  </label>
                  <input
                    type="text"
                    value={createFormData.source_name}
                    onChange={(e) => handleCreateFormChange('source_name', e.target.value)}
                    placeholder="SEC Filing"
                    className="admin-quality-input"
                  />
                </div>

                <div className="admin-quality-field">
                  <label className="admin-quality-label">
                    Source URL
                  </label>
                  <input
                    type="url"
                    value={createFormData.source_url}
                    onChange={(e) => handleCreateFormChange('source_url', e.target.value)}
                    placeholder="https://..."
                    className="admin-quality-input"
                  />
                </div>

                <div className="admin-quality-field admin-quality-field-full">
                  <label className="admin-quality-label">
                    Source Notes
                  </label>
                  <textarea
                    value={createFormData.source_notes}
                    onChange={(e) => handleCreateFormChange('source_notes', e.target.value)}
                    rows={2}
                    placeholder="Additional source information..."
                    className="admin-quality-textarea"
                  />
                </div>

                <div className="admin-quality-field admin-quality-field-full">
                  <label className="admin-quality-label">
                    Admin Notes
                  </label>
                  <textarea
                    value={createFormData.admin_notes}
                    onChange={(e) => handleCreateFormChange('admin_notes', e.target.value)}
                    rows={2}
                    placeholder="Internal admin notes..."
                    className="admin-quality-textarea"
                  />
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="admin-quality-btn-primary"
                >
                  {createLoading ? 'Creating...' : 'Create Corporate Action'}
                </button>
              </div>
            </form>
          </div>

          {/* Corporate Actions List */}
          {corporateActionsLoading ? (
            <p className="admin-quality-loading">Loading corporate actions...</p>
          ) : corporateActionsError ? (
            <div className="admin-quality-error">Error: {corporateActionsError}</div>
          ) : corporateActions.length === 0 ? (
            <p className="admin-quality-empty">No corporate actions found.</p>
          ) : (
            <div className="admin-quality-table-wrapper">
              <table className="admin-quality-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Action Type</th>
                  <th>Old Ticker</th>
                  <th>New Ticker</th>
                  <th>Effective Date</th>
                  <th>Conversion Ratio</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Preview</th>
                </tr>
              </thead>
              <tbody>
                {corporateActions.map((action) => (
                  <React.Fragment key={action.id}>
                    <tr>
                      <td>{action.id}</td>
                      <td>{actionTypeLabels[action.action_type] || action.action_type}</td>
                      <td>{action.old_ticker}</td>
                      <td>{action.new_ticker || '-'}</td>
                      <td>{action.effective_date}</td>
                      <td>{action.conversion_ratio !== null && action.conversion_ratio !== undefined ? action.conversion_ratio : '-'}</td>
                      <td>{action.status}</td>
                      <td>{action.created_at ? new Date(action.created_at).toLocaleDateString() : '-'}</td>
                      <td>
                        <button
                          type="button"
                          className="admin-quality-btn-secondary"
                          onClick={() => handleDryRunClick(action.id)}
                          disabled={dryRunLoading[action.id]}
                        >
                          {dryRunLoading[action.id] ? 'Loading...' : expandedDryRun === action.id ? 'Collapse' : 'Dry Run'}
                        </button>
                      </td>
                    </tr>
                    {expandedDryRun === action.id && (
                      <tr>
                        <td colSpan="10" style={{ padding: 0 }}>
                          <div className="admin-quality-dryrun-panel">
                            {dryRunError[action.id] ? (
                              <div className="admin-quality-error">
                                Error: {dryRunError[action.id]}
                              </div>
                            ) : dryRunCache[action.id] ? (
                              <>
                                <div className="admin-quality-dryrun-header">
                                  <h3>Dry-Run Preview: {dryRunCache[action.id].corporate_action.old_ticker}</h3>
                                  <p className="admin-quality-dryrun-subtitle">
                                    Read-only impact preview - no changes will be applied
                                  </p>
                                </div>

                                {dryRunCache[action.id].warnings && dryRunCache[action.id].warnings.length > 0 && (
                                  <div className="admin-quality-dryrun-warnings">
                                    <h4>⚠️ Warnings</h4>
                                    <ul>
                                      {dryRunCache[action.id].warnings.map((warning, idx) => (
                                        <li key={idx}>{warning}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}

                                <div className="admin-quality-dryrun-summary">
                                  <div className="admin-quality-dryrun-summary-grid">
                                    <div>
                                      <p><strong>Action Type:</strong> {actionTypeLabels[dryRunCache[action.id].corporate_action.action_type] || dryRunCache[action.id].corporate_action.action_type}</p>
                                      <p><strong>Old Ticker:</strong> {dryRunCache[action.id].corporate_action.old_ticker}</p>
                                      <p><strong>New Ticker:</strong> {dryRunCache[action.id].corporate_action.new_ticker || 'N/A'}</p>
                                      <p><strong>Effective Date:</strong> {dryRunCache[action.id].corporate_action.effective_date}</p>
                                      <p><strong>Conversion Ratio:</strong> {dryRunCache[action.id].corporate_action.conversion_ratio || 'N/A'}</p>
                                    </div>
                                    <div>
                                      <p><strong>Affected Users:</strong> {dryRunCache[action.id].affected_users_count}</p>
                                      <p><strong>Affected Portfolios:</strong> {dryRunCache[action.id].affected_portfolios_count}</p>
                                      <p><strong>Affected Holdings:</strong> {dryRunCache[action.id].affected_holdings_count}</p>
                                    </div>
                                  </div>
                                </div>

                                {dryRunCache[action.id].holdings && dryRunCache[action.id].holdings.length > 0 && (
                                  <div className="admin-quality-dryrun-holdings">
                                    <h4>Holdings Preview ({dryRunCache[action.id].holdings.length} holdings)</h4>
                                    <div style={{ overflowX: 'auto' }}>
                                      <table className="admin-quality-dryrun-table">
                                        <thead>
                                          <tr>
                                            <th>User</th>
                                            <th>Portfolio</th>
                                            <th>Current Ticker</th>
                                            <th>New Ticker</th>
                                            <th style={{ textAlign: 'right' }}>Current Qty</th>
                                            <th style={{ textAlign: 'right' }}>Preview Qty</th>
                                            <th style={{ textAlign: 'right' }}>Current Avg Price</th>
                                            <th style={{ textAlign: 'right' }}>Preview Avg Price</th>
                                            <th style={{ textAlign: 'right' }}>Old Price</th>
                                            <th style={{ textAlign: 'right' }}>New Price</th>
                                            <th style={{ textAlign: 'right' }}>Current Value</th>
                                            <th style={{ textAlign: 'right' }}>Preview Value</th>
                                            <th>Notes</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {dryRunCache[action.id].holdings.map((holding, idx) => (
                                            <tr key={idx}>
                                              <td>{holding.user_email}</td>
                                              <td>{holding.portfolio_name}</td>
                                              <td>{holding.current_ticker}</td>
                                              <td>{holding.new_ticker || '-'}</td>
                                              <td style={{ textAlign: 'right' }}>{holding.current_quantity !== null && holding.current_quantity !== undefined ? holding.current_quantity.toFixed(4) : '-'}</td>
                                              <td style={{ textAlign: 'right' }}>{holding.preview_quantity !== null && holding.preview_quantity !== undefined ? holding.preview_quantity.toFixed(4) : '-'}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.current_average_buy_price)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.preview_average_buy_price)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.old_latest_price)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.new_latest_price)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.estimated_current_market_value)}</td>
                                              <td style={{ textAlign: 'right' }}>{formatPrice(holding.estimated_preview_market_value)}</td>
                                              <td className="theme-text-muted">{holding.notes || '-'}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>
                                )}

                                {/* Apply Section */}
                                {(dryRunCache[action.id].corporate_action.status !== 'applied' || applyResult[action.id]) &&
                                 applySupportedActionTypes.includes(dryRunCache[action.id].corporate_action.action_type) && (
                                  <div className="admin-quality-dryrun-apply">
                                    <h4>Apply Corporate Action</h4>
                                    
                                    {applySuccess[action.id] ? (
                                      <div className="admin-quality-success">
                                        {applySuccess[action.id]}
                                      </div>
                                    ) : null}

                                    {applyError[action.id] ? (
                                      <div className="admin-quality-error">
                                        {applyError[action.id]}
                                      </div>
                                    ) : null}

                                    {!applyResult[action.id] ? (
                                      <div style={{ marginTop: '1rem' }}>
                                        <button
                                          type="button"
                                          onClick={() => handleApplyAction(action.id)}
                                          disabled={applyLoading[action.id]}
                                          className="admin-quality-btn-primary"
                                        >
                                          {applyLoading[action.id] ? 'Applying...' : 'Apply Corporate Action'}
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="admin-quality-dryrun-result">
                                        <h4>Apply Result</h4>
                                        <div className="admin-quality-dryrun-summary">
                                          <div className="admin-quality-dryrun-summary-grid">
                                            <div>
                                              <p><strong>Corporate Action Status:</strong> {applyResult[action.id].corporate_action?.status || 'N/A'}</p>
                                              <p><strong>Affected Users:</strong> {applyResult[action.id].affected_users_count || 0}</p>
                                              <p><strong>Affected Portfolios:</strong> {applyResult[action.id].affected_portfolios_count || 0}</p>
                                              <p><strong>Affected Holdings:</strong> {applyResult[action.id].affected_holdings_count || 0}</p>
                                              <p><strong>Updated Holdings:</strong> {applyResult[action.id].updated_holdings_count || 0}</p>
                                            </div>
                                          </div>
                                        </div>

                                        {applyResult[action.id].holdings_before_after && applyResult[action.id].holdings_before_after.length > 0 && (
                                          <div style={{ marginTop: '1rem' }}>
                                            <h4>Holdings Before/After ({applyResult[action.id].holdings_before_after.length} holdings)</h4>
                                            <div style={{ overflowX: 'auto' }}>
                                              <table className="admin-quality-dryrun-table">
                                                <thead>
                                                  <tr>
                                                    <th>User</th>
                                                    <th>Portfolio</th>
                                                    <th>Old Ticker</th>
                                                    <th>New Ticker</th>
                                                    <th style={{ textAlign: 'right' }}>Qty Before</th>
                                                    <th style={{ textAlign: 'right' }}>Qty After</th>
                                                    <th style={{ textAlign: 'right' }}>Avg Price Before</th>
                                                    <th style={{ textAlign: 'right' }}>Avg Price After</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {applyResult[action.id].holdings_before_after.map((holding, idx) => (
                                                    <tr key={idx}>
                                                      <td>{holding.user_email}</td>
                                                      <td>{holding.portfolio_name}</td>
                                                      <td>{holding.old_ticker}</td>
                                                      <td>{holding.new_ticker}</td>
                                                      <td style={{ textAlign: 'right' }}>{holding.quantity_before !== null && holding.quantity_before !== undefined ? holding.quantity_before.toFixed(4) : '-'}</td>
                                                      <td style={{ textAlign: 'right' }}>{holding.quantity_after !== null && holding.quantity_after !== undefined ? holding.quantity_after.toFixed(4) : '-'}</td>
                                                      <td style={{ textAlign: 'right' }}>{formatPrice(holding.average_buy_price_before)}</td>
                                                      <td style={{ textAlign: 'right' }}>{formatPrice(holding.average_buy_price_after)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDataQuality;
